import { useEffect, useState } from "react";
import { Bot, MessageSquare, RefreshCw, Trash2, User } from "lucide-react";
import { AuthError, deleteConversation, getConversation, listConversations } from "./api";
import type { ChatConversation, ChatMessageRow } from "./types";
import "./chats.css";

function formatWhen(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export function ChatsView({ onAuthError }: { onAuthError: () => void }) {
  const [items, setItems] = useState<ChatConversation[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessageRow[]>([]);
  const [msgState, setMsgState] = useState<"idle" | "loading" | "ready">("idle");
  const [deleting, setDeleting] = useState<string | null>(null);

  const open = async (id: string) => {
    setSelectedId(id);
    setMsgState("loading");
    try {
      const data = await getConversation(id);
      setMessages(data.messages);
      setMsgState("ready");
    } catch (err) {
      if (err instanceof AuthError) return onAuthError();
      setMessages([]);
      setMsgState("ready");
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Usunąć tę rozmowę na stałe? Tej operacji nie można cofnąć.")) return;
    setDeleting(id);
    try {
      await deleteConversation(id);
      setItems((prev) => prev.filter((c) => c.id !== id));
      if (selectedId === id) {
        setSelectedId(null);
        setMessages([]);
        setMsgState("idle");
      }
    } catch (err) {
      if (err instanceof AuthError) return onAuthError();
    } finally {
      setDeleting(null);
    }
  };

  const load = async () => {
    setState("loading");
    try {
      const list = await listConversations();
      setItems(list);
      setState("ready");
      if (list.length && !selectedId) void open(list[0].id);
    } catch (err) {
      if (err instanceof AuthError) return onAuthError();
      setState("error");
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === "loading") {
    return <div className="crm-state">Ładowanie rozmów…</div>;
  }

  if (state === "error") {
    return (
      <div className="crm-state crm-state--error">
        <p>Nie udało się pobrać rozmów.</p>
        <button type="button" className="crm-btn crm-btn--primary" onClick={() => void load()}>
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="chatlog-empty">
        <MessageSquare size={28} aria-hidden="true" />
        <p>Brak rozmów. Pojawią się tu, gdy ktoś napisze do Wirtualnego Asystenta na stronie.</p>
      </div>
    );
  }

  const selected = items.find((c) => c.id === selectedId) || null;

  return (
    <div className="chatlog">
      <aside className="chatlog-list">
        <div className="chatlog-list-head">
          <span>{items.length} rozmów</span>
          <button type="button" className="crm-icon-btn" onClick={() => void load()} title="Odśwież">
            <RefreshCw size={16} aria-hidden="true" />
          </button>
        </div>
        <ul>
          {items.map((conv) => (
            <li key={conv.id}>
              <button
                type="button"
                className={`chatlog-item${selectedId === conv.id ? " is-active" : ""}`}
                onClick={() => void open(conv.id)}
              >
                <span className="chatlog-item-q">{conv.firstQuestion || "(rozmowa bez pytania)"}</span>
                <span className="chatlog-item-meta">
                  {formatWhen(conv.lastAt)} · {conv.messageCount} wiad.
                  {conv.ipCountry ? ` · ${conv.ipCountry}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="chatlog-thread">
        {selected ? (
          <>
            <div className="chatlog-thread-head">
              <span>
                {formatWhen(selected.lastAt)} · {selected.messageCount} wiad.
                {selected.ipCountry ? ` · ${selected.ipCountry}` : ""}
              </span>
              <button
                type="button"
                className="chatlog-del"
                onClick={() => void remove(selected.id)}
                disabled={deleting === selected.id}
                title="Usuń tę rozmowę na stałe"
              >
                <Trash2 size={15} aria-hidden="true" />
                {deleting === selected.id ? "Usuwanie…" : "Usuń rozmowę"}
              </button>
            </div>
            <div className="chatlog-thread-scroll">
              {msgState === "loading" && <div className="crm-state">Ładowanie rozmowy…</div>}
              {msgState === "ready" && !messages.length && (
                <div className="crm-state">Ta rozmowa nie ma jeszcze treści.</div>
              )}
              {msgState === "ready" &&
                messages.map((msg) => (
                  <div key={msg.id} className={`chatlog-msg ${msg.role}`}>
                    <span className="chatlog-msg-icon" aria-hidden="true">
                      {msg.role === "user" ? <User size={15} /> : <Bot size={15} />}
                    </span>
                    <div className="chatlog-msg-body">
                      <div className="chatlog-msg-role">
                        {msg.role === "user" ? "Klient" : "Asystent"}
                        <span>{formatWhen(msg.createdAt)}</span>
                      </div>
                      <p>{msg.content}</p>
                    </div>
                  </div>
                ))}
            </div>
          </>
        ) : (
          <div className="crm-state">Wybierz rozmowę z listy, aby zobaczyć jej treść.</div>
        )}
      </section>
    </div>
  );
}
